import { getErrorStatus } from '~/utils/http/api-error'

// Awaits `useAsyncData` and, if the fetch failed, escalates the error to Nuxt's
// error page: `throw createError(...)` swaps the whole route for `app/error.vue`,
// which renders `4xx.vue`/`500.vue` and reports non-404s to AppInsights.
//
// USE SPARINGLY. Reach for this only where a failed fetch genuinely breaks the
// whole page -- i.e. the data IS the page. In a component, a failed fetch should
// normally degrade locally (empty state, retry) and leave the rest of the page
// working; use `useLazyAsyncData` with your own error state there instead.
// Escalating from a component takes down the page around it, and takes any retry
// affordance with it. See AB#62909.
//
// NOT related to nuxt2's `useAsyncDataWithError` (StampinUp.Ordering.Frontend,
// `composables/action.ts`) despite the identical old name. That one fires an
// AppInsights trace for the unknown-locale case and then passes straight through
// to `useLazyAsyncData` -- its `createError` branch is commented out, and nuxt2
// has no `createError`/`showError` in `composables/` or `pages/` at all. A failed
// fetch there leaves the error on the ref and renders a degraded page. Porting a
// nuxt2 call site to this helper verbatim CHANGES its failure behaviour.
//
// This wrapper is NOT about SSR/client error parity. Core Nuxt already handles
// that: `error` is backed by the serialized `payload._errors[key]` and normalized
// through `createError()` in the same `execute()` on both server and client
// (verified in nuxt 4.5.1 and in the installed @nuxt/bridge -- the mechanism is
// identical, so it did not arrive with Nuxt 4).
//
// Returns the same `AsyncData` shape as `useAsyncData`, so existing destructuring
// (`pending`, `data`, `refresh`) continues to work. Note that `await` suspends
// `setup`, so `pending` is already `false` by first paint -- bind a loading UI to
// it only if you know the call site re-fetches.

export async function useAsyncDataOrErrorPage<T> (key: string, handler: () => Promise<T>) {
  const asyncData = await useAsyncData<T>(key, handler)

  if (asyncData.error.value) {
    throw createError(asyncData.error.value)
  }

  return asyncData
}

// The body of the server-side `onServerPrefetch` hook registered by
// `useLazyAsyncDataOrErrorPage` below, named and exported so it can be tested.
// It cannot be reached through the hook from a spec: vitest runs vue's client
// build, where `onServerPrefetch` is compiled out to a no-op that never
// populates `instance.sp`, so the registered callback is unreachable -- and
// unmockable, the auto-imported hook resolving past both `vi.mock('vue')` and
// `mockNuxtImport`. Verified; see AB#63036.
//
// Awaiting the handle is what settles the fetch: `useLazyAsyncData` registers
// its own `onServerPrefetch` during setup, so by the time this resolves the
// outcome is known and `error` is final. Reading `error` before the await would
// see the pre-fetch value and let a broken page render.
// The default preserves the established all-errors contract. A page whose data
// can degrade locally may provide a filter for the terminal errors that truly
// require the Nuxt error page; both server and client paths use the same filter.
type AsyncDataErrorFilter = (error: unknown) => boolean
const alwaysEscalateAsyncDataError: AsyncDataErrorFilter = error => !!error

/**
 * Escalates only a 404 — missing CMS content — to the error page, and leaves every other fetch
 * failure local to the page.
 *
 * For CMS-backed pages whose route is always registered, so the only way to hide content that
 * does not exist (or is outside its publish window) is to raise a 404 from the fetch. Other
 * failures are transient and should not replace the page with an error screen.
 */
export const escalateMissingContent: AsyncDataErrorFilter = error =>
  getErrorStatus(error) === 404

export async function escalateAsyncDataError (
  asyncData: PromiseLike<unknown> & { error: { value: unknown } },
  shouldEscalate: AsyncDataErrorFilter = alwaysEscalateAsyncDataError,
) {
  await asyncData
  if (asyncData.error.value && shouldEscalate(asyncData.error.value)) {
    throw createError(asyncData.error.value as Parameters<typeof createError>[0])
  }
}

// Non-awaiting sibling of `useAsyncDataOrErrorPage`: same escalation to the
// error page, but does NOT suspend `setup`, so `pending` is true on the first
// render of a client-side navigation and a `:loading="pending"` overlay
// actually gets to show. That is the bug this exists for -- `await` settles the
// fetch before first paint, so every overlay bound to `pending` was dead.
//
// USE SPARINGLY. Without an escalation filter it still takes the whole page
// down on any failure. In a component, prefer plain `useLazyAsyncData` with
// local error state so the failure degrades locally. See AB#62911.
//
// The error is surfaced through two paths because the fetch settles at a
// different point in the lifecycle on each side:
//
//   * Server -- `useLazyAsyncData` still starts the fetch during `setup` and
//     registers its own `onServerPrefetch` to await it, so the outcome is known
//     before the component renders. Registering a second `onServerPrefetch`
//     after it lets us `throw createError(...)` while there is still a render to
//     abort, exactly like the awaited form.
//   * Client -- the initial fetch is deferred to `onBeforeMount`, so the error
//     arrives after `setup` has returned and there is nothing left to throw
//     into. `showError` is Nuxt's client-side entry point to the same error
//     page. Watching rather than checking once also means a failed `refresh()`
//     surfaces, which the awaited form never did.
export function useLazyAsyncDataOrErrorPage<T> (
  key: string,
  handler: () => Promise<T>,
  shouldEscalate: AsyncDataErrorFilter = alwaysEscalateAsyncDataError,
) {
  const asyncData = useLazyAsyncData<T>(key, handler)

  // `isServer` rather than `import.meta.server` so this branch is reachable from
  // a spec -- the inlined literal is not. See `~/utils/env` and AB#63036.
  if (isServer) {
    if (getCurrentInstance()) {
      onServerPrefetch(() => escalateAsyncDataError(asyncData, shouldEscalate))
    }
  } else {
    watch(asyncData.error, (error) => {
      if (error && shouldEscalate(error)) {
        showError(createError(error))
      }
    }, { immediate: true })
  }

  return asyncData
}
